/* =========================================================
   shop.genspark.ai — Support Center · Direction A
   Drop into your Shopify theme at: assets/support.js
   Load with: {{ 'support.js' | asset_url | script_tag }}
   =========================================================

   Auth strategy: Shopify native customer object (Liquid).
   SC_CONFIG.customer is injected server-side:
     - Logged in  → customer.orders[] already populated, Step 1 shows a
                    dropdown of real orders; no API calls needed.
     Step 1: Collect order number + email as plain text fields.
   No API call, no login required. Values stored in state and
   submitted as part of the support ticket.
*/
(function () {
  'use strict';

  const CFG = window.SC_CONFIG || {};
  const root = document.querySelector('[data-sc-root]');
  if (!root) return console.warn('[support] no [data-sc-root] found');

  // =========================================================
  // i18n — translations keyed by Shopify locale iso_code
  // Supported: en (default), ja, ko
  // =========================================================
  const STRINGS = {
    en: {
      // Wizard header
      submitRequest:      'Submit a request',
      submitRequestSub:   "Fill in the details below and we'll get back to you.",
      // Step labels
      stepOrder:          'Order',
      stepTopic:          'Topic',
      stepDescription:    'Description',
      stepAttachments:    'Attachments',
      stepReview:         'Review',
      // Step 1 — Order
      orderNumber:        'Order number',
      orderNumberHint:    'Find it in your order confirmation email.',
      orderNumberPH:      'e.g. Genspark#0001',
      email:              'Email',
      emailPH:            'you@example.com',
      errBothFields:      'Please enter both order number and email.',
      errInvalidEmail:    'Please enter a valid email address.',
      // Step 2 — Topic
      chooseTopicErr:     'Please choose a topic.',
      // Step 3 — Details
      whatsWrong:         "What's wrong?",
      describeIssue:      'Describe the issue',
      describePH:         "Include when the problem started, what triggers it, and anything you've tried…",
      describeHint:       'The more detail, the faster we can resolve it.',
      preferredRes:       'Preferred resolution',
      relatedArticle:     'Related article — this might help',
      errSelectReason:    'Please select what happened.',
      errDescTooShort:    'Please describe the issue in a bit more detail (10+ characters).',
      // Resolutions
      resReplacement:     'Replacement (same item)',
      resRefund:          'Full refund',
      resCredit:          'Store credit',
      resAdvise:          'Advise me',
      // Step 4 — Attachments
      dropFiles:          'Drop files here or',
      browse:             'browse',
      uploadHint:         'Images (JPG, PNG, HEIC) or short video (MP4, MOV) · up to 50 MB each',
      uploadRequired:     'required',
      uploadTips:         'Tips — Include the item, any visible damage, and the original packaging. A 5–10 second video is often clearer than photos.',
      fileTooLarge:       'is over 50 MB — skipped.',
      errPhotoRequired:   'This category requires at least one photo or video.',
      // Step 5 — Review
      reviewCategory:     'Category',
      reviewOrder:        'Order',
      reviewWhatsWrong:   "What's wrong",
      reviewDescription:  'Description',
      reviewResolution:   'Resolution',
      reviewAttachments:  'Attachments',
      reviewPhotos:       'photos',
      reviewVideo:        'video',
      reviewEdit:         'Edit',
      consentText:        "I agree to Genspark Shop's",
      consentPolicy:      'Return & Refund Policy',
      consentTail:        'and consent to processing of the information above to resolve my request.',
      errConsent:         'Please agree to the policy before submitting.',
      submitBtn:          'Submit request',
      submitting:         'Submitting…',
      errSubmit:          'Submission failed. Please try again.',
      // Buttons / nav
      back:               'Back',
      continue:           'Continue',
      // Success
      successTitle:       'Request submitted',
      successHasFiles:    'Your ticket is ready. Click below to download the email file, then open it and send to',
      successNoFiles:     'Click below to send your ticket to',
      successSlaPrefix:   "We'll reply within",
      downloadEml:        'Download email file (with attachments)',
      sendEmail:          'Send email',
      copyTicket:         'Copy ticket #',
      fallbackLabel:      'Or send manually to:',
      copyAddr:           'Copy',
      nextStepsTitle:     'What happens next',
      nextStepEml:        'Open the downloaded .eml file in your email client, confirm attachments, then send.',
      nextStepMail:       'Click Send email — your email client will open with everything pre-filled.',
      nextStepReply:      'Our team will reply within {sla} after receiving your message.',
      nextStepTrack:      'You can check progress anytime with your ticket number and email.',
      toastDownloaded:    'Downloaded — open the .eml file to send.',
      toastOpening:       'Opening email client…',
      toastCopiedTicket:  'Copied ticket #',
      toastCopiedEmail:   'Copied email address',
      ticketLabel:        'Your ticket number',
      slaLabel:           'SLA',
      priorityLabel:      'Priority',
      priorityHigh:       'High',
      priorityNormal:     'Normal',
      // Email body labels
      bodyTicketId:       'Ticket ID',
      bodySubmitted:      'Submitted',
      bodyCategory:       'Category',
      bodyOrderNo:        'Order No.',
      bodyEmail:          'Email',
      bodyPriority:       'Priority',
      bodyReason:         'Reason',
      bodyResolution:     'Resolution',
      bodySla:            'SLA',
      bodyIssueDesc:      'Issue description',
      bodyAttachments:    'Attachments',
      bodyNotProvided:    '(not provided)',
      bodyNotSelected:    '(not selected)',
      bodyPage:           'Page',
      // Categories
      categories: {
        return:    { label: 'Return & Refund',          desc: 'Send an item back for a refund',
          reasons: ['Changed my mind','Item not as described','Wrong size / fit','Received extra items','Other'] },
        exchange:  { label: 'Exchange (Size / Color)',  desc: 'Swap for a different size or color',
          reasons: ['Wrong size','Wrong color','Fit issue','Other'] },
        damaged:   { label: 'Damaged / Defective',      desc: 'Item arrived broken or has a defect',
          reasons: ['Broken on arrival','Manufacturing defect','Missing parts','Malfunctioning','Other'] },
        shipping:  { label: 'Not Received / Shipping',  desc: "Order hasn't arrived or tracking is stuck",
          reasons: ['Tracking not updating','Marked delivered — not received','Overdue delivery','Address issue'] },
        wrong:     { label: 'Wrong / Missing Item',     desc: "You received something you didn't order",
          reasons: ['Wrong item shipped','Missing item from order','Wrong variant','Extra item received'] },
        inquiry:   { label: 'Product Question',         desc: 'Sizing, materials, compatibility, stock',   reasons: [] },
        billing:   { label: 'Account / Payment',        desc: 'Charges, refunds, invoices, login',
          reasons: ['Duplicate charge','Refund not received','Invoice request','Coupon / discount','Login / account'] },
        other:     { label: 'Feedback & Other',         desc: 'Complaint, suggestion, or anything else',
          reasons: ['Complaint','Suggestion','Compliment','Other'] },
      },
    },

    ja: {
      submitRequest:      'お問い合わせを送信',
      submitRequestSub:   '以下の内容を入力してください。折り返しご連絡いたします。',
      stepOrder:          '注文情報',
      stepTopic:          'トピック',
      stepDescription:    '詳細',
      stepAttachments:    '添付ファイル',
      stepReview:         '確認',
      orderNumber:        '注文番号',
      orderNumberHint:    '注文確認メールでご確認ください。',
      orderNumberPH:      '例：Genspark#0001',
      email:              'メールアドレス',
      emailPH:            'you@example.com',
      errBothFields:      '注文番号とメールアドレスを両方入力してください。',
      errInvalidEmail:    '有効なメールアドレスを入力してください。',
      chooseTopicErr:     'トピックを選択してください。',
      whatsWrong:         '問題の種類',
      describeIssue:      '詳細を記入してください',
      describePH:         '問題が発生した時期、再現条件、試した対処法などを記入してください…',
      describeHint:       '詳しく書いていただくほど、迅速に対応できます。',
      preferredRes:       '希望する対応方法',
      relatedArticle:     '関連記事 — 参考になるかもしれません',
      errSelectReason:    '問題の種類を選択してください。',
      errDescTooShort:    '詳細を10文字以上で記入してください。',
      resReplacement:     '同一商品と交換',
      resRefund:          '全額返金',
      resCredit:          'ストアクレジット',
      resAdvise:          'アドバイスを求める',
      dropFiles:          'ここにファイルをドロップ、または',
      browse:             '参照',
      uploadHint:         '画像（JPG・PNG・HEIC）または短い動画（MP4・MOV）· 各50MBまで',
      uploadRequired:     '必須',
      uploadTips:         'ヒント — 商品・破損箇所・元の梱包を含めてください。5〜10秒の動画は写真より分かりやすい場合があります。',
      fileTooLarge:       'は50MBを超えているためスキップされました。',
      errPhotoRequired:   'この種類のお問い合わせには、写真または動画が1枚以上必要です。',
      reviewCategory:     'カテゴリー',
      reviewOrder:        '注文',
      reviewWhatsWrong:   '問題の種類',
      reviewDescription:  '詳細',
      reviewResolution:   '希望する対応',
      reviewAttachments:  '添付ファイル',
      reviewPhotos:       '枚の写真',
      reviewVideo:        '本の動画',
      reviewEdit:         '編集',
      consentText:        'Genspark Shopの',
      consentPolicy:      '返品・返金ポリシー',
      consentTail:        'に同意し、上記情報の処理に同意します。',
      errConsent:         'ポリシーに同意してから送信してください。',
      submitBtn:          'リクエストを送信',
      submitting:         '送信中…',
      errSubmit:          '送信に失敗しました。もう一度お試しください。',
      back:               '戻る',
      continue:           '次へ',
      successTitle:       'リクエストを受け付けました',
      successHasFiles:    'チケットが作成されました。下のボタンでメールファイルをダウンロードし、開いて送信してください。送信先：',
      successNoFiles:     '下のボタンをクリックしてチケットを送信してください。送信先：',
      successSlaPrefix:   '以内にご返信いたします：',
      downloadEml:        'メールファイルをダウンロード（添付ファイル付き）',
      sendEmail:          'メールを送信',
      copyTicket:         'チケット番号をコピー',
      fallbackLabel:      '手動で送信する場合：',
      copyAddr:           'コピー',
      nextStepsTitle:     '次のステップ',
      nextStepEml:        'ダウンロードした .eml ファイルをメールクライアントで開き、添付ファイルを確認してから送信してください。',
      nextStepMail:       '「メールを送信」をクリックすると、内容が入力済みのメールクライアントが開きます。',
      nextStepReply:      'メール受信後、{sla}以内にご返信いたします。',
      nextStepTrack:      'チケット番号とメールアドレスでいつでも進捗を確認できます。',
      toastDownloaded:    'ダウンロード完了 — .eml ファイルを開いて送信してください。',
      toastOpening:       'メールクライアントを開いています…',
      toastCopiedTicket:  'チケット番号をコピーしました：',
      toastCopiedEmail:   'メールアドレスをコピーしました',
      ticketLabel:        'チケット番号',
      slaLabel:           'SLA',
      priorityLabel:      '優先度',
      priorityHigh:       '高',
      priorityNormal:     '通常',
      // Email body labels
      bodyTicketId:       'チケット番号',
      bodySubmitted:      '提出日時',
      bodyCategory:       'カテゴリー',
      bodyOrderNo:        '注文番号',
      bodyEmail:          'メールアドレス',
      bodyPriority:       '優先度',
      bodyReason:         '問題の種類',
      bodyResolution:     '希望する対応',
      bodySla:            'SLA',
      bodyIssueDesc:      '詳細内容',
      bodyAttachments:    '添付ファイル',
      bodyNotProvided:    '（未入力）',
      bodyNotSelected:    '（未選択）',
      bodyPage:           'ページ',
      categories: {
        return:    { label: '返品・返金',            desc: '商品を返送して返金を申請する',
          reasons: ['気が変わった','商品説明と異なる','サイズ・フィットが違う','余分な商品が届いた','その他'] },
        exchange:  { label: 'サイズ・カラー交換',    desc: '別のサイズまたはカラーに交換する',
          reasons: ['サイズが違う','カラーが違う','フィット感の問題','その他'] },
        damaged:   { label: '破損・不良品',          desc: '商品が破損して届いた、または不良がある',
          reasons: ['到着時に破損','製造不良','部品不足','動作不良','その他'] },
        shipping:  { label: '未着・配送問題',        desc: '注文が届かない、または追跡が止まっている',
          reasons: ['追跡が更新されない','配達済みになっているが未着','配送遅延','住所の問題'] },
        wrong:     { label: '商品の誤送・不足',      desc: '注文と異なる商品が届いた',
          reasons: ['誤った商品が届いた','注文の商品が不足','バリアントが違う','余分な商品が届いた'] },
        inquiry:   { label: '商品に関する質問',      desc: 'サイズ・素材・互換性・在庫について',   reasons: [] },
        billing:   { label: 'アカウント・支払い',    desc: '請求・返金・請求書・ログインについて',
          reasons: ['二重請求','返金が届かない','請求書の発行','クーポン・割引','ログイン・アカウント'] },
        other:     { label: 'フィードバック・その他', desc: 'クレーム・提案・その他',
          reasons: ['クレーム','提案','称賛','その他'] },
      },
    },

    ko: {
      submitRequest:      '문의 제출',
      submitRequestSub:   '아래 내용을 입력하시면 빠르게 답변 드리겠습니다.',
      stepOrder:          '주문 정보',
      stepTopic:          '문의 유형',
      stepDescription:    '상세 내용',
      stepAttachments:    '첨부 파일',
      stepReview:         '검토',
      orderNumber:        '주문 번호',
      orderNumberHint:    '주문 확인 이메일에서 확인하실 수 있습니다.',
      orderNumberPH:      '예: Genspark#0001',
      email:              '이메일',
      emailPH:            'you@example.com',
      errBothFields:      '주문 번호와 이메일을 모두 입력해 주세요.',
      errInvalidEmail:    '올바른 이메일 주소를 입력해 주세요.',
      chooseTopicErr:     '문의 유형을 선택해 주세요.',
      whatsWrong:         '문제 유형',
      describeIssue:      '문제를 설명해 주세요',
      describePH:         '문제가 발생한 시점, 재현 조건, 시도한 해결 방법 등을 입력해 주세요…',
      describeHint:       '자세히 작성할수록 빠르게 해결할 수 있습니다.',
      preferredRes:       '원하시는 해결 방법',
      relatedArticle:     '관련 도움말 — 참고가 될 수 있습니다',
      errSelectReason:    '문제 유형을 선택해 주세요.',
      errDescTooShort:    '문제를 10자 이상으로 설명해 주세요.',
      resReplacement:     '동일 상품으로 교환',
      resRefund:          '전액 환불',
      resCredit:          '스토어 크레딧',
      resAdvise:          '상담 요청',
      dropFiles:          '파일을 여기에 드래그하거나',
      browse:             '찾아보기',
      uploadHint:         '이미지(JPG·PNG·HEIC) 또는 짧은 동영상(MP4·MOV) · 각 50MB 이하',
      uploadRequired:     '필수',
      uploadTips:         '팁 — 상품, 손상 부위, 원래 포장을 포함해 주세요. 5~10초 동영상이 사진보다 명확한 경우가 많습니다.',
      fileTooLarge:       '은(는) 50MB를 초과하여 건너뛰었습니다.',
      errPhotoRequired:   '이 문의 유형은 사진 또는 동영상이 최소 1개 필요합니다.',
      reviewCategory:     '카테고리',
      reviewOrder:        '주문',
      reviewWhatsWrong:   '문제 유형',
      reviewDescription:  '상세 내용',
      reviewResolution:   '희망 해결 방법',
      reviewAttachments:  '첨부 파일',
      reviewPhotos:       '장의 사진',
      reviewVideo:        '개의 동영상',
      reviewEdit:         '수정',
      consentText:        'Genspark Shop의',
      consentPolicy:      '반품·환불 정책',
      consentTail:        '에 동의하며, 위 정보의 처리에 동의합니다.',
      errConsent:         '정책에 동의한 후 제출해 주세요.',
      submitBtn:          '문의 제출',
      submitting:         '제출 중…',
      errSubmit:          '제출에 실패했습니다. 다시 시도해 주세요.',
      back:               '뒤로',
      continue:           '계속',
      successTitle:       '문의가 접수되었습니다',
      successHasFiles:    '티켓이 생성되었습니다. 아래 버튼으로 이메일 파일을 다운로드한 후 열어서 보내주세요. 수신처：',
      successNoFiles:     '아래 버튼을 클릭하여 티켓을 보내주세요. 수신처：',
      successSlaPrefix:   '이내에 답변 드리겠습니다：',
      downloadEml:        '이메일 파일 다운로드（첨부 파일 포함）',
      sendEmail:          '이메일 보내기',
      copyTicket:         '티켓 번호 복사',
      fallbackLabel:      '직접 보내실 경우：',
      copyAddr:           '복사',
      nextStepsTitle:     '다음 단계',
      nextStepEml:        '다운로드한 .eml 파일을 이메일 클라이언트로 열고 첨부 파일을 확인한 후 보내주세요.',
      nextStepMail:       '이메일 보내기를 클릭하면 내용이 미리 입력된 이메일 클라이언트가 열립니다.',
      nextStepReply:      '이메일 수신 후 {sla} 이내에 답변 드리겠습니다.',
      nextStepTrack:      '티켓 번호와 이메일로 언제든지 진행 상황을 확인하실 수 있습니다.',
      toastDownloaded:    '다운로드 완료 — .eml 파일을 열어 보내주세요.',
      toastOpening:       '이메일 클라이언트를 여는 중…',
      toastCopiedTicket:  '티켓 번호가 복사되었습니다：',
      toastCopiedEmail:   '이메일 주소가 복사되었습니다',
      ticketLabel:        '티켓 번호',
      slaLabel:           'SLA',
      priorityLabel:      '우선순위',
      priorityHigh:       '높음',
      priorityNormal:     '보통',
      // Email body labels
      bodyTicketId:       '티켓 번호',
      bodySubmitted:      '제출 일시',
      bodyCategory:       '카테고리',
      bodyOrderNo:        '주문 번호',
      bodyEmail:          '이메일',
      bodyPriority:       '우선순위',
      bodyReason:         '문제 유형',
      bodyResolution:     '희망 해결 방법',
      bodySla:            'SLA',
      bodyIssueDesc:      '상세 내용',
      bodyAttachments:    '첨부 파일',
      bodyNotProvided:    '(미입력)',
      bodyNotSelected:    '(미선택)',
      bodyPage:           '페이지',
      categories: {
        return:    { label: '반품·환불',           desc: '상품을 반송하고 환불 신청',
          reasons: ['마음이 바뀌었습니다','상품 설명과 다릅니다','사이즈·핏이 다릅니다','여분의 상품이 배송되었습니다','기타'] },
        exchange:  { label: '사이즈·색상 교환',    desc: '다른 사이즈 또는 색상으로 교환',
          reasons: ['사이즈가 다릅니다','색상이 다릅니다','핏 문제','기타'] },
        damaged:   { label: '파손·불량품',         desc: '상품이 파손되어 도착했거나 불량이 있습니다',
          reasons: ['도착 시 파손','제조 불량','부품 누락','작동 불량','기타'] },
        shipping:  { label: '미배송·배송 문제',    desc: '주문이 도착하지 않거나 추적이 멈췄습니다',
          reasons: ['추적이 업데이트되지 않음','배송 완료로 표시되었으나 미수령','배송 지연','주소 문제'] },
        wrong:     { label: '잘못된·누락된 상품', desc: '주문하지 않은 상품이 배송되었습니다',
          reasons: ['잘못된 상품 배송','주문 상품 누락','잘못된 옵션','여분의 상품 배송'] },
        inquiry:   { label: '상품 문의',           desc: '사이즈·소재·호환성·재고 문의',   reasons: [] },
        billing:   { label: '계정·결제',           desc: '청구·환불·영수증·로그인 관련',
          reasons: ['중복 청구','환불 미수령','영수증 발급','쿠폰·할인','로그인·계정'] },
        other:     { label: '피드백·기타',         desc: '불만·제안·기타 문의',
          reasons: ['불만','제안','칭찬','기타'] },
      },
    },
  };

  // Resolve locale: use Shopify locale, strip region (e.g. "en-US" → "en"), fall back to "en"
  const rawLocale = (CFG.locale || 'en').split('-')[0].toLowerCase();
  const LANG = STRINGS[rawLocale] || STRINGS.en;

  // Translation helper
  const T = (key, vars) => {
    let s = LANG[key] || STRINGS.en[key] || key;
    if (vars) Object.keys(vars).forEach((k) => { s = s.replace('{' + k + '}', vars[k]); });
    return s;
  };

  // Merge translated category labels/descs/reasons into CFG.categories at runtime
  CFG.categories.forEach((cat) => {
    const tr = (LANG.categories || {})[cat.id] || (STRINGS.en.categories || {})[cat.id];
    if (tr) {
      cat.label   = tr.label;
      cat.desc    = tr.desc;
      if (tr.reasons && tr.reasons.length) cat.reasons = tr.reasons;
    }
  });

  // ---------- State ----------
  const state = {
    screen: 'wizard',        // wizard | success | track-lookup | track
    categoryId: null,
    step: 0,                 // 0: Order, 1: Topic, 2: Details, 3: [Attachments], 4: Review
    order: null,             // { id, email, items[] }
    selectedItems: {},       // { itemId: true }
    reason: '',
    description: '',
    resolution: '',
    files: [],               // { name, size, kind, url?, uploading? }
    consent: false,
    result: null,            // { ticketId, sla, priority } after submit
    trackingData: null,      // fetched from tracking API
  };

  // ---------- Helpers ----------
  const $  = (sel, el = root) => el.querySelector(sel);
  const $$ = (sel, el = root) => Array.from(el.querySelectorAll(sel));
  const h  = (html) => { const t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild; };
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const fmt = { size: (b) => b < 1024 ? b + ' B' : b < 1024*1024 ? (b/1024).toFixed(1)+' KB' : (b/1024/1024).toFixed(1)+' MB' };
  const toast = (msg, err) => {
    const el = $('#sc-toast');
    el.textContent = msg;
    el.classList.toggle('is-error', !!err);
    el.classList.add('is-shown');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => el.classList.remove('is-shown'), 3200);
  };
  const getCategory = () => CFG.categories.find((c) => c.id === state.categoryId);

  // ---------- SVG icons (inline for perf) ----------
  const ICONS = {
    Search:      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>',
    Question:    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 015 0c0 2-2.5 2-2.5 4"/><path d="M12 17.5v.5"/></svg>',
    ChevronRight:'<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>',
    ChevronLeft: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg>',
    ArrowRight:  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    Check:       '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>',
    BigCheck:    '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>',
    Clock:       '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    Upload:      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M6 10l6-6 6 6"/><path d="M4 20h16"/></svg>',
    Image:       '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="M4 18l5-5 4 4 3-3 4 4"/></svg>',
    Video:       '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="14" height="14" rx="2"/><path d="M17 10l4-2v8l-4-2z"/></svg>',
    X:           '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    Sparkle:     '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v6M12 14v6M4 12h6M14 12h6"/></svg>',
    Lock:        '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>',
    Send:        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l17-8-8 17-2-7z"/></svg>',
    Refresh:     '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12a8 8 0 0114-5.3L21 9"/><path d="M20 12a8 8 0 01-14 5.3L3 15"/><path d="M21 4v5h-5M3 20v-5h5"/></svg>',
    Package:     '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/><path d="M12 11v10"/></svg>',
    Alert:       '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l10 18H2z"/><path d="M12 10v5M12 18v.5"/></svg>',
    Truck:       '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="12" height="9"/><path d="M14 10h4l3 3v3h-7"/><circle cx="6" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/></svg>',
    Chat:        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h16v11H8l-4 4z"/></svg>',
    Card:        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18"/></svg>',
  };
  const iconOf = (name) => ICONS[name] || ICONS.Question;

  // =========================================================
  // Rendering
  // =========================================================
  function render() {
    // Clean up any detached file inputs from previous Attachments step
    document.querySelectorAll('input[type="file"][style*="opacity:0"]').forEach(el => el.remove());
    const mount = $('#sc-view');
    mount.innerHTML = '';
    let screen;
    if      (state.screen === 'wizard')        screen = renderWizard();
    else if (state.screen === 'success')       screen = renderSuccess();
    else if (state.screen === 'track-lookup')  screen = renderTrackLookup();
    else if (state.screen === 'track')         screen = renderTrack();
    mount.appendChild(screen);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ---------- Landing (FAQ + Categories) ----------
  // ---------- Wizard ----------

  function renderWizard() {
    const steps = wizardSteps();
    const stepFn = steps[state.step];
    const cat = getCategory(); // null until Topic step is done

    // Title & subtitle: show category name once chosen, generic otherwise
    const titleText = cat ? cat.label : T('submitRequest');
    const subtitleText = cat ? cat.desc : T('submitRequestSub');

    const el = h(`
      <div class="sc-screen is-active">
        <div class="sc-shell sc-shell--narrow">
          <h2 style="font-size:22px;margin-top:12px;">${escapeHtml(titleText)}</h2>
          <p style="color:var(--sc-ink-3);margin-top:6px;font-size:14px;">${escapeHtml(subtitleText)}</p>
          <div class="sc-steps" id="sc-steps"></div>
          <div id="sc-step-body"></div>
        </div>
      </div>
    `);

    // Step indicator
    const stepEl = el.querySelector('#sc-steps');
    steps.forEach((s, i) => {
      const cls = i < state.step ? 'is-done' : i === state.step ? 'is-active' : '';
      const item = h(`
        <div class="sc-steps-item ${cls}">
          <span class="sc-steps-num">${i < state.step ? iconOf('Check') : (i + 1)}</span>
          <span class="sc-steps-label">${s.label}</span>
        </div>
      `);
      stepEl.appendChild(item);
      if (i < steps.length - 1) {
        stepEl.appendChild(h(`<div class="sc-steps-bar ${i < state.step ? 'is-done' : ''}"></div>`));
      }
    });

    // Step body
    el.querySelector('#sc-step-body').appendChild(stepFn.render());

    return el;
  }

  // Fixed steps: Order → Topic → Details → [Attachments] → Review
  // Attachments slot is included only when the chosen category requires photos.
  function wizardSteps() {
    const cat = getCategory();
    const needsPhotos = cat && new Set(cat.fields || []).has('photos');
    const steps = [
      { label: T('stepOrder'),       render: renderStepIdentify },
      { label: T('stepTopic'),       render: renderStepTopic },
      { label: T('stepDescription'), render: renderStepDetails },
    ];
    if (needsPhotos) {
      steps.push({ label: T('stepAttachments'), render: renderStepAttachments });
    }
    steps.push({ label: T('stepReview'), render: renderStepReview });
    return steps;
  }

  function nextButtonRow(primaryLabel, onPrimary) {
    if (primaryLabel === undefined) primaryLabel = T('continue');
    const row = h(`
      <div class="sc-nav-row">
        <button class="sc-btn sc-btn-ghost sc-btn-sm" type="button" data-back-inline>${iconOf('ChevronLeft')} ${T('back')}</button>
        <button class="sc-btn sc-btn-primary" type="button" data-next>${escapeHtml(primaryLabel)} ${iconOf('ArrowRight')}</button>
      </div>
    `);
    row.querySelector('[data-back-inline]').addEventListener('click', () => {
      if (state.step > 0) state.step -= 1;
      render();
    });
    row.querySelector('[data-next]').addEventListener('click', onPrimary);
    return row;
  }

  // ---- Step 1: Order info ----
  function renderStepIdentify() {
    const el = h(`
      <div class="sc-card sc-card-pad">
        <div class="sc-field">
          <label class="sc-label" for="sc-order">${T('orderNumber')} <span class="sc-req">*</span></label>
          <input class="sc-input sc-mono" id="sc-order" placeholder="${escapeHtml(T('orderNumberPH'))}"
                 value="${escapeHtml(state.order?.id || '')}" autocomplete="off">
          <div class="sc-hint">${T('orderNumberHint')}</div>
        </div>
        <div class="sc-field">
          <label class="sc-label" for="sc-email">${T('email')} <span class="sc-req">*</span></label>
          <input class="sc-input" id="sc-email" type="email" placeholder="${escapeHtml(T('emailPH'))}"
                 value="${escapeHtml(state.order?.email || '')}" autocomplete="email">
        </div>
        <div id="sc-lookup-error" class="sc-error" hidden></div>
      </div>
    `);
    const wrap = document.createElement('div');
    wrap.appendChild(el);
    // Step 1 has no Back (it’s the first step)
    const row = h(`
      <div class="sc-nav-row" style="justify-content:flex-end;">
        <button class="sc-btn sc-btn-primary" type="button" data-next>${T('continue')} ${iconOf('ArrowRight')}</button>
      </div>
    `);
    row.querySelector('[data-next]').addEventListener('click', () => {
      const orderId = el.querySelector('#sc-order').value.trim();
      const email   = el.querySelector('#sc-email').value.trim();
      const errEl   = el.querySelector('#sc-lookup-error');
      errEl.hidden  = true;
      if (!orderId || !email) {
        errEl.textContent = T('errBothFields');
        errEl.hidden = false; return;
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        errEl.textContent = T('errInvalidEmail');
        errEl.hidden = false; return;
      }
      state.order = { id: orderId, email: email, items: [] };
      state.step += 1;
      render();
    });
    wrap.appendChild(row);
    return wrap;
  }

  // ---- Step 2: Choose a topic ----
  function renderStepTopic() {
    const wrap = document.createElement('div');
    const grid = h(`<div class="sc-grid-2" id="sc-topic-grid"></div>`);
    CFG.categories.forEach((c) => {
      const tile = h(`
        <button class="sc-tile ${state.categoryId === c.id ? 'is-selected' : ''}" type="button" data-cat="${c.id}">
          <span class="sc-tile-icon">${iconOf(c.icon)}</span>
          <span class="sc-tile-body">
            <span class="sc-tile-label">${escapeHtml(c.label)}</span>
            <span class="sc-tile-desc">${escapeHtml(c.desc)}</span>
          </span>
          <span class="sc-tile-chevron">${iconOf('ChevronRight')}</span>
        </button>
      `);
      tile.addEventListener('click', () => {
        // Reset topic-dependent state when switching
        state.categoryId = c.id;
        state.reason = '';
        state.description = '';
        state.resolution = '';
        state.files = [];
        state.step += 1;
        render();
      });
      grid.appendChild(tile);
    });
    wrap.appendChild(grid);
    const errDiv = h(`<div class="sc-error" hidden>${T('chooseTopicErr')}</div>`);
    wrap.appendChild(errDiv);
    // Back button only (no Continue — clicking a tile advances automatically)
    const backRow = h(`
      <div class="sc-nav-row" style="justify-content:flex-start;">
        <button class="sc-btn sc-btn-ghost sc-btn-sm" type="button">${iconOf('ChevronLeft')} ${T('back')}</button>
      </div>
    `);
    backRow.querySelector('button').addEventListener('click', () => { state.step -= 1; render(); });
    wrap.appendChild(backRow);
    return wrap;
  }

  // ---- Step: Details ----
  function renderStepDetails() {
    const cat = getCategory();
    const wrap = document.createElement('div');
    const card = h(`
      <div class="sc-card sc-card-pad">
        ${cat.reasons && cat.reasons.length ? `
          <div class="sc-field">
            <label class="sc-label">${T('whatsWrong')} <span class="sc-req">*</span></label>
            <div class="sc-radio-group" id="sc-reasons"></div>
          </div>
        ` : ''}
        <div class="sc-field">
          <label class="sc-label" for="sc-desc">${T('describeIssue')} <span class="sc-req">*</span></label>
          <textarea class="sc-textarea" id="sc-desc" rows="5"
            placeholder="${escapeHtml(T('describePH'))}">${escapeHtml(state.description || '')}</textarea>
          <div class="sc-hint">${T('describeHint')}</div>
        </div>
        <div class="sc-field" id="sc-resolution-wrap" ${cat.id === 'return' || cat.id === 'damaged' || cat.id === 'wrong' ? '' : 'hidden'}>
          <label class="sc-label">${T('preferredRes')}</label>
          <div class="sc-radio-group sc-radio-group--2col" id="sc-resolutions"></div>
        </div>
      </div>
    `);

    // Reasons
    if (cat.reasons && cat.reasons.length) {
      const rWrap = card.querySelector('#sc-reasons');
      cat.reasons.forEach((r) => {
        const row = radioRow('reason', r, state.reason === r);
        row.addEventListener('click', () => {
          state.reason = r;
          rWrap.querySelectorAll('.sc-radio').forEach((x) => x.classList.remove('is-checked'));
          row.classList.add('is-checked');
        });
        rWrap.appendChild(row);
      });
    }

    // Resolutions
    const resWrap = card.querySelector('#sc-resolutions');
    if (resWrap) {
      const resList = [T('resReplacement'), T('resRefund'), T('resCredit'), T('resAdvise')];
      resList.forEach((r) => {
        const row = radioRow('resolution', r, state.resolution === r);
        row.addEventListener('click', () => {
          state.resolution = r;
          resWrap.querySelectorAll('.sc-radio').forEach((x) => x.classList.remove('is-checked'));
          row.classList.add('is-checked');
        });
        resWrap.appendChild(row);
      });
    }
    wrap.appendChild(card);

    // Smart FAQ (keyword-based)
    const smart = h(`<div id="sc-smart-faq"></div>`);
    wrap.appendChild(smart);
    const desc = card.querySelector('#sc-desc');
    const updateSmart = () => {
      const q = desc.value.toLowerCase();
      const hit = CFG.faqs.find((f) => (f.keywords || []).some((k) => q.includes(k)));
      smart.innerHTML = '';
      if (hit && q.length > 10) {
        smart.appendChild(h(`
          <div class="sc-smart-faq">
            <div class="sc-smart-faq-icon">${iconOf('Sparkle')}</div>
            <div style="flex:1;">
              <div class="sc-smart-faq-title">${T('relatedArticle')}</div>
              <div class="sc-smart-faq-body"><b>${escapeHtml(hit.q)}</b> — ${escapeHtml(hit.a)}</div>
            </div>
          </div>
        `));
      }
    };
    desc.addEventListener('input', updateSmart);
    updateSmart();

    const err = h(`<div class="sc-error" hidden></div>`);
    wrap.appendChild(err);

    wrap.appendChild(nextButtonRow(T('continue'), () => {
      state.description = desc.value.trim();
      if (cat.reasons && cat.reasons.length && !state.reason) {
        err.textContent = T('errSelectReason');
        err.hidden = false; return;
      }
      if (state.description.length < 10) {
        err.textContent = T('errDescTooShort');
        err.hidden = false; return;
      }
      state.step += 1; render();
    }));
    return wrap;
  }

  function radioRow(name, val, checked) {
    return h(`
      <label class="sc-radio ${checked ? 'is-checked' : ''}">
        <span class="sc-radio-dot"></span>
        <span class="sc-radio-label">${escapeHtml(val)}</span>
        <input type="radio" name="${name}" value="${escapeHtml(val)}" ${checked ? 'checked' : ''}>
      </label>
    `);
  }

  // ---- Step: Attachments ----
  function renderStepAttachments() {
    const cat = getCategory();
    const wrap = document.createElement('div');
    const card = h(`
      <div class="sc-card sc-card-pad">
        <div class="sc-uploader" role="button" tabindex="0" id="sc-drop-zone">
          <div class="sc-uploader-icon">${iconOf('Upload')}</div>
          <div class="sc-uploader-label">${T('dropFiles')} <b style="color:var(--sc-accent);cursor:pointer;">${T('browse')}</b></div>
          <div class="sc-uploader-hint">
            ${T('uploadHint')}
            ${cat.requiresPhoto ? '· <span class="sc-req">' + T('uploadRequired') + '</span>' : ''}
          </div>
        </div>
        <div class="sc-file-list" id="sc-file-list"></div>
        <div class="sc-tips">
          ${T('uploadTips')}
        </div>
      </div>
    `);
    wrap.appendChild(card);

    // Hidden file input — kept outside the visible DOM to avoid layout glitches
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*,video/*';
    input.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(input);

    const listEl = card.querySelector('#sc-file-list');
    const dropzone = card.querySelector('#sc-drop-zone');

    // Click or keyboard on dropzone → open file picker
    dropzone.addEventListener('click', () => input.click());
    dropzone.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); input.click(); } });

    const refreshList = () => {
      listEl.innerHTML = '';
      state.files.forEach((f, idx) => {
        const row = h(`
          <div class="sc-file">
            <div class="sc-file-icon">${iconOf(f.kind === 'video' ? 'Video' : 'Image')}</div>
            <div style="flex:1;min-width:0;">
              <div class="sc-file-name">${escapeHtml(f.name)}</div>
              ${f.uploading ? `<div class="sc-file-progress"><div class="sc-file-progress-bar" style="width:${f.progress || 0}%"></div></div>` : ''}
            </div>
            <div class="sc-file-size">${escapeHtml(fmt.size(f.size))}</div>
            <button class="sc-file-x" type="button" aria-label="Remove">${iconOf('X')}</button>
          </div>
        `);
        row.querySelector('.sc-file-x').addEventListener('click', () => {
          state.files.splice(idx, 1); refreshList();
        });
        listEl.appendChild(row);
      });
    };
    refreshList();

    const handleFiles = (files) => {
      for (const file of files) {
        if (file.size > 50 * 1024 * 1024) {
          toast(`${file.name} ${T('fileTooLarge')}`, true);
          continue;
        }
        // 直接保存 File 对象，提交时读取为 base64 附件嵌入 .eml
        const rec = {
          name: file.name, size: file.size,
          kind: file.type.startsWith('video/') ? 'video' : 'image',
          mimeType: file.type || 'application/octet-stream',
          file,          // 原始 File 对象，提交时转 base64
          uploading: false,
        };
        state.files.push(rec);
        refreshList();
      }
    };

    input.addEventListener('change', (e) => handleFiles(Array.from(e.target.files)));

    ['dragenter', 'dragover'].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.classList.add('is-drag'); });
    });
    ['dragleave', 'drop'].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.classList.remove('is-drag'); });
    });
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      handleFiles(Array.from(e.dataTransfer.files));
    });

    const err = h(`<div class="sc-error" hidden></div>`);
    wrap.appendChild(err);

    wrap.appendChild(nextButtonRow(T('continue'), () => {
      // 文件直接存在内存，无需等待上传
      const uploading = false;
      if (cat.requiresPhoto && state.files.length === 0) {
        err.textContent = T('errPhotoRequired'); err.hidden = false; return;
      }
      state.step += 1; render();
    }));
    return wrap;
  }

  // ---- Step: Review & submit ----
  function renderStepReview() {
    const cat = getCategory();
    const order = state.order || {};
    const selItems = (order.items || []).filter((i) => state.selectedItems[i.id]);
    const rows = [
      [T('reviewCategory'),    cat.label],
      [T('reviewOrder'),       order.id ? `${order.id} · ${order.email || ''}` : '—'],
      cat.reasons && cat.reasons.length ? [T('reviewWhatsWrong'), state.reason || '—'] : null,
      [T('reviewDescription'), state.description],
      state.resolution ? [T('reviewResolution'), state.resolution] : null,
      state.files.length ? [T('reviewAttachments'), `${state.files.filter((f) => f.kind === 'image').length} ${T('reviewPhotos')}, ${state.files.filter((f) => f.kind === 'video').length} ${T('reviewVideo')}`] : null,
    ].filter(Boolean);

    const wrap = document.createElement('div');
    const card = h(`<div class="sc-card"></div>`);
    rows.forEach(([k, v]) => {
      const row = h(`
        <div class="sc-review-row">
          <div class="sc-review-key">${escapeHtml(k)}</div>
          <div class="sc-review-val">${escapeHtml(String(v))}</div>
          <a class="sc-review-edit" href="#" data-edit>${T('reviewEdit')}</a>
        </div>
      `);
      card.appendChild(row);
    });

    const consent = h(`
      <div class="sc-consent">
        <div class="sc-consent-check ${state.consent ? 'is-checked' : ''}" role="checkbox" tabindex="0" aria-checked="${state.consent}"></div>
        <div class="sc-consent-text">
          ${T('consentText')} <a href="/policies/refund-policy">${T('consentPolicy')}</a>
          ${T('consentTail')}
        </div>
      </div>
    `);
    const check = consent.querySelector('.sc-consent-check');
    const toggleConsent = () => {
      state.consent = !state.consent;
      check.classList.toggle('is-checked', state.consent);
      check.setAttribute('aria-checked', String(state.consent));
    };
    check.addEventListener('click', toggleConsent);
    check.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleConsent(); } });
    card.appendChild(consent);
    wrap.appendChild(card);

    card.querySelectorAll('[data-edit]').forEach((a, i) => {
      a.addEventListener('click', (e) => { e.preventDefault(); state.step = Math.max(0, state.step - 1); render(); });
    });

    const err = h(`<div class="sc-error" hidden></div>`);
    wrap.appendChild(err);

    const row = h(`
      <div class="sc-nav-row">
        <button class="sc-btn sc-btn-ghost sc-btn-sm" type="button" data-back-inline>${iconOf('ChevronLeft')} ${T('back')}</button>
        <button class="sc-btn sc-btn-accent sc-btn-lg" type="button" data-submit>${T('submitBtn')} ${iconOf('Send')}</button>
      </div>
    `);
    row.querySelector('[data-back-inline]').addEventListener('click', () => { state.step -= 1; render(); });
    row.querySelector('[data-submit]').addEventListener('click', async () => {
      if (!state.consent) { err.textContent = T('errConsent'); err.hidden = false; return; }
      const btn = row.querySelector('[data-submit]');
      btn.disabled = true;
      btn.innerHTML = '<span class="sc-spinner"></span> ' + T('submitting');
      try {
        const result = await submitTicket();
        state.result = result;
        state.screen = 'success';
        render();
      } catch (e2) {
        err.textContent = e2.message || T('errSubmit'); err.hidden = false;
        btn.disabled = false;
        btn.innerHTML = T('submitBtn') + ' ' + iconOf('Send');
      }
    });
    wrap.appendChild(row);
    return wrap;
  }

  // ---------- Success ----------
  function renderSuccess() {
    const r = state.result || { ticketId: 'T-????', sla: '24 hours', priority: 'Normal' };
    const supportEmail = 'support@genspark.ai';
    const hasFiles = !!state._emlContent;  // 有附件时显示下载按钮

    const heroText = hasFiles
      ? `${T('successHasFiles')} <b>${escapeHtml(supportEmail)}</b>.`
      : `${T('successNoFiles')} <b>${escapeHtml(supportEmail)}</b>.`;

    const priorityDisp = r.priority === 'High' ? T('priorityHigh') : T('priorityNormal');

    const el = h(`
      <div class="sc-screen is-active">
        <div class="sc-shell" style="max-width:560px;">
          <div class="sc-success-hero">
            <div class="sc-success-check">${iconOf('BigCheck')}</div>
            <h1>${T('successTitle')}</h1>
            <p>${heroText}</p>
          </div>
          <div class="sc-card sc-ticket-card">
            <div class="sc-eyebrow">${T('ticketLabel')}</div>
            <div class="sc-ticket-num sc-mono">${escapeHtml(r.ticketId)}</div>
            <div class="sc-ticket-meta">
              <span>${iconOf('Clock')} ${T('slaLabel')} · ${escapeHtml(r.sla)}</span>
              <span>·</span>
              <span>${T('priorityLabel')} · ${escapeHtml(priorityDisp)}</span>
            </div>
            <div class="sc-ticket-actions">
              ${hasFiles
                ? `<button class="sc-btn sc-btn-primary" data-download-eml>${iconOf('Upload')} ${T('downloadEml')}</button>`
                : `<button class="sc-btn sc-btn-primary" data-open-mail>${T('sendEmail')} ${iconOf('Send')}</button>`
              }
              <button class="sc-btn sc-btn-secondary" data-copy>${T('copyTicket')}${escapeHtml(r.ticketId)}</button>
            </div>
          </div>
          <div class="sc-mailto-fallback">
            <p>${T('fallbackLabel')}</p>
            <div class="sc-mailto-row">
              <span class="sc-mailto-addr">${escapeHtml(supportEmail)}</span>
              <button class="sc-btn sc-btn-ghost sc-btn-sm" data-copy-email>${T('copyAddr')}</button>
            </div>
          </div>
          <div class="sc-next-steps">
            <b>${T('nextStepsTitle')}</b>
            <ol>
              ${hasFiles
                ? `<li>${T('nextStepEml')}</li>`
                : `<li>${T('nextStepMail')}</li>`
              }
              <li>${T('nextStepReply', { sla: escapeHtml(r.sla) })}</li>
              <li>${T('nextStepTrack')}</li>
            </ol>
          </div>
        </div>
      </div>
    `);

    // 下载 .eml 文件（含附件）
    const downloadBtn = el.querySelector('[data-download-eml]');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => {
        const blob = new Blob([state._emlContent], { type: 'message/rfc822' });
        const url  = URL.createObjectURL(blob);
        const dl   = document.createElement('a');
        dl.href     = url;
        dl.download  = `${state._mailSubject}.eml`;
        dl.style.display = 'none';
        document.body.appendChild(dl);
        dl.click();
        document.body.removeChild(dl);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        toast(T('toastDownloaded'));
      });
    }

    // 发送邮件（无附件时）
    const openMailBtn = el.querySelector('[data-open-mail]');
    if (openMailBtn) {
      openMailBtn.addEventListener('click', () => {
        const mailtoHref =
          'mailto:support@genspark.ai' +
          '?subject=' + encodeURIComponent(state._mailSubject || '') +
          '&body='    + encodeURIComponent(state._bodyText || '');
        const a = document.createElement('a');
        a.href = mailtoHref;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast(T('toastOpening'));
      });
    }

    // 复制工单号
    el.querySelector('[data-copy]').addEventListener('click', () => {
      navigator.clipboard.writeText(r.ticketId).then(() => toast(T('toastCopiedTicket') + r.ticketId));
    });

    // 复制邮件地址（兜底方案）
    el.querySelector('[data-copy-email]').addEventListener('click', () => {
      navigator.clipboard.writeText(supportEmail).then(() => toast(T('toastCopiedEmail')));
    });

    // Auto-trigger: open mailto or download .eml as soon as the success screen appears
    setTimeout(() => {
      if (hasFiles) {
        // Auto-download the .eml file
        const blob = new Blob([state._emlContent], { type: 'message/rfc822' });
        const url  = URL.createObjectURL(blob);
        const dl   = document.createElement('a');
        dl.href     = url;
        dl.download = `${state._mailSubject}.eml`;
        dl.style.display = 'none';
        document.body.appendChild(dl);
        dl.click();
        document.body.removeChild(dl);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        toast(T('toastDownloaded'));
      } else {
        // Auto-open mailto
        const mailtoHref =
          'mailto:support@genspark.ai' +
          '?subject=' + encodeURIComponent(state._mailSubject || '') +
          '&body='    + encodeURIComponent(state._bodyText || '');
        const a = document.createElement('a');
        a.href = mailtoHref;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast(T('toastOpening'));
      }
    }, 400);

    return el;
  }

  // ---------- Track lookup ----------
  function renderTrackLookup() {
    const el = h(`
      <div class="sc-screen is-active">
        <div class="sc-lookup">
          <h2>Track a request</h2>
          <p>Enter your ticket number and the email you used to submit.</p>
          <div class="sc-field">
            <label class="sc-label">Ticket number</label>
            <input class="sc-input sc-mono" id="sc-t-id" placeholder="T-XXXX">
          </div>
          <div class="sc-field">
            <label class="sc-label">Email</label>
            <input class="sc-input" id="sc-t-em" placeholder="you@example.com">
          </div>
          <div class="sc-error" id="sc-t-err" hidden></div>
          <div class="sc-nav-row">
            <a href="#" data-back-home style="font-size:13px;color:var(--sc-ink-3);">← Support Center</a>
            <button class="sc-btn sc-btn-primary" data-look>Look up ${iconOf('ArrowRight')}</button>
          </div>
        </div>
      </div>
    `);
    el.querySelector('[data-back-home]').addEventListener('click', (e) => { e.preventDefault(); state.screen = 'landing'; render(); });
    el.querySelector('[data-look]').addEventListener('click', async () => {
      const id = el.querySelector('#sc-t-id').value.trim();
      const em = el.querySelector('#sc-t-em').value.trim();
      const errEl = el.querySelector('#sc-t-err');
      errEl.hidden = true;
      if (!id || !em) { errEl.textContent = 'Please enter both.'; errEl.hidden = false; return; }
      const btn = el.querySelector('[data-look]');
      btn.disabled = true;
      btn.innerHTML = '<span class="sc-spinner"></span> Looking up…';
      try {
        await loadTracking(id, em);
        if (!state.trackingData) throw new Error('not found');
        state.screen = 'track';
        render();
      } catch (e2) {
        errEl.textContent = 'No matching ticket found. Check the number and email.';
        errEl.hidden = false;
        btn.disabled = false;
        btn.innerHTML = 'Look up ' + iconOf('ArrowRight');
      }
    });
    return el;
  }

  // ---------- Tracking view ----------
  function renderTrack() {
    const t = state.trackingData || {};
    const el = h(`
      <div class="sc-screen is-active">
        <div class="sc-shell">
          <a class="sc-back" href="#" data-back-home>${iconOf('ChevronLeft')} Support Center</a>
          <div class="sc-track-head">
            <div>
              <div class="sc-eyebrow">Ticket</div>
              <div class="sc-track-id">${escapeHtml(t.ticketId || 'T-????')}</div>
              <div class="sc-track-cat">${escapeHtml(t.category || '')} · ${escapeHtml(t.subject || '')}</div>
            </div>
            <span class="sc-chip ${statusToChip(t.status)}"><span class="sc-chip-dot"></span>${escapeHtml(t.status || 'Unknown')}</span>
          </div>

          <div class="sc-card sc-card-pad" style="margin-top:20px;">
            <div class="sc-row-head" style="margin-top:0;">
              <h3 style="font-size:14px;">Progress</h3>
              <span class="sc-muted">${t.eta ? 'ETA · ' + escapeHtml(t.eta) : ''}</span>
            </div>
            <div class="sc-timeline" id="sc-timeline"></div>
          </div>

          <div class="sc-card sc-card-pad" style="margin-top:16px;">
            <h3 style="font-size:14px;margin-bottom:12px;">Messages</h3>
            <div id="sc-msgs"></div>
            <div class="sc-composer">
              <textarea id="sc-reply" placeholder="Reply…" rows="2"></textarea>
              <div class="sc-composer-foot">
                <div class="sc-composer-icons">${iconOf('Image')} ${iconOf('Video')}</div>
                <button class="sc-btn sc-btn-primary sc-btn-sm" data-send>Send reply</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);
    el.querySelector('[data-back-home]').addEventListener('click', (e) => { e.preventDefault(); state.screen = 'landing'; render(); });

    // Timeline
    const timelineEl = el.querySelector('#sc-timeline');
    (t.timeline || []).forEach((it) => {
      const item = h(`
        <div class="sc-tl-item is-${it.state || 'pending'}">
          <div class="sc-tl-dot">${it.state === 'done' ? iconOf('Check') : ''}</div>
          <div class="sc-tl-label">${escapeHtml(it.label)}</div>
          ${it.detail ? `<div class="sc-tl-detail">${escapeHtml(it.detail)}</div>` : ''}
          ${it.time ? `<div class="sc-tl-time">${escapeHtml(it.time)}</div>` : ''}
        </div>
      `);
      timelineEl.appendChild(item);
    });

    // Messages
    const msgs = el.querySelector('#sc-msgs');
    (t.messages || []).forEach((m) => {
      msgs.appendChild(h(`
        <div class="sc-msg ${m.agent ? 'is-agent' : ''}">
          <div class="sc-msg-head">
            <span class="sc-msg-who">${escapeHtml(m.who)}</span>
            <span>${escapeHtml(m.time)}</span>
          </div>
          <div class="sc-msg-body">${escapeHtml(m.body)}</div>
        </div>
      `));
    });

    // Reply
    el.querySelector('[data-send]').addEventListener('click', async () => {
      const text = el.querySelector('#sc-reply').value.trim();
      if (!text) return;
      const btn = el.querySelector('[data-send]');
      btn.disabled = true;
      try {
        await postReply(t.ticketId, state.order?.email || t.email, text);
        toast('Reply sent');
        el.querySelector('#sc-reply').value = '';
        await loadTracking(t.ticketId, state.order?.email || t.email);
        render();
      } catch (e2) {
        toast('Send failed: ' + e2.message, true);
        btn.disabled = false;
      }
    });
    return el;
  }

  function statusToChip(s) {
    if (s === 'Resolved') return 'success';
    if (s === 'In progress') return 'warning';
    if (s === 'Awaiting customer') return 'info';
    if (s === 'New') return 'info';
    return '';
  }

  // =========================================================
  // API calls
  // =========================================================

  async function uploadToCloudinary(file, onProgress) {
    if (!CFG.cloudinary || !CFG.cloudinary.cloudName) {
      // Dev mode: fake it after a delay
      await new Promise((res) => {
        let p = 0;
        const iv = setInterval(() => {
          p += 20; onProgress && onProgress(p);
          if (p >= 100) { clearInterval(iv); res(); }
        }, 100);
      });
      return 'https://res.cloudinary.com/demo/image/upload/sample.jpg';
    }
    const url = `https://api.cloudinary.com/v1_1/${CFG.cloudinary.cloudName}/${file.type.startsWith('video/') ? 'video' : 'image'}/upload`;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', CFG.cloudinary.uploadPreset);
    fd.append('folder', 'shop-support');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress && onProgress(Math.round(e.loaded / e.total * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const res = JSON.parse(xhr.responseText);
            resolve(res.secure_url);
          } catch (e) { reject(new Error('Cloudinary response error')); }
        } else reject(new Error('Upload failed (' + xhr.status + ')'));
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(fd);
    });
  }

  // 将 File 对象读取为 base64 字符串（不含 data:xxx;base64, 前缀）
  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(new Error('读取文件失败：' + file.name));
      reader.readAsDataURL(file);
    });
  }

  // 生成符合 RFC 2822 的 .eml 文件内容（multipart/mixed，含附件）
  async function buildEml(subject, bodyText, fileRecs) {
    const boundary = 'sc_boundary_' + Date.now() + '_' + Math.random().toString(36).slice(2);
    const CRLF = '\r\n';

    // 将正文折行（RFC 2822 建议每行不超过 76 字符，此处用 \r\n 分隔即可）
    const encodedBody = bodyText.split('\n').join(CRLF);

    let eml = [
      'MIME-Version: 1.0',
      `To: support@genspark.ai`,
      `Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=`,
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
      '',
      `--${boundary}`,
      'Content-Type: text/plain; charset=UTF-8',
      'Content-Transfer-Encoding: quoted-printable',
      '',
      encodedBody,
    ].join(CRLF);

    // 逐个附件：读取 base64 并追加 MIME part
    for (const rec of fileRecs) {
      if (!rec.file) continue;
      const b64 = await fileToBase64(rec.file);
      const mime = rec.mimeType || 'application/octet-stream';
      // RFC 5987 encoded filename (简单起见，用 Base64 编码文件名)
      const encodedName = `=?UTF-8?B?${btoa(unescape(encodeURIComponent(rec.name)))}?=`;
      // 每 76 字符插一个 CRLF（base64 折行）
      const foldedB64 = b64.match(/.{1,76}/g).join(CRLF);
      eml += CRLF + [
        `--${boundary}`,
        `Content-Type: ${mime}; name="${encodedName}"`,
        `Content-Transfer-Encoding: base64`,
        `Content-Disposition: attachment; filename="${encodedName}"`,
        '',
        foldedB64,
      ].join(CRLF);
    }

    eml += CRLF + `--${boundary}--` + CRLF;
    return eml;
  }

  async function submitTicket() {
    const cat      = getCategory();
    const order    = state.order || {};
    const priority = cat.requiresPhoto ? 'High' : 'Normal';
    const ticketId = 'T-' + Math.floor(1000 + Math.random() * 9000);

    const dateStr = new Date().toISOString().slice(0, 10);
    const subject = `Genspark shop Support&Feedback ${dateStr} ${ticketId}`;

    // Build body using locale-matched labels via T()
    // Pad label to fixed width for monospace alignment
    const pad = (label, width) => label + ' '.repeat(Math.max(1, width - label.length));
    const W = 16; // column width for labels

    const submittedTime = new Date().toLocaleString(
      rawLocale === 'ja' ? 'ja-JP' : rawLocale === 'ko' ? 'ko-KR' : 'en-US',
      { timeZone: 'Asia/Shanghai', hour12: false }
    ) + ' (CST)';

    const priorityDisp = priority === 'High' ? T('priorityHigh') : T('priorityNormal');

    const bodyLines = [
      `${pad(T('bodyTicketId'), W)}${ticketId}`,
      `${pad(T('bodySubmitted'), W)}${submittedTime}`,
      ``,
      `----------------------------------------`,
      `${pad(T('bodyCategory'), W)}${cat.label}`,
      `${pad(T('bodyOrderNo'),  W)}${order.id    || T('bodyNotProvided')}`,
      `${pad(T('bodyEmail'),    W)}${order.email || T('bodyNotProvided')}`,
      `${pad(T('bodyPriority'), W)}${priorityDisp}`,
      `${pad(T('bodyResolution'), W)}${state.resolution || T('bodyNotSelected')}`,
      `${pad(T('bodySla'),      W)}${cat.sla}`,
      ``,
      `${T('bodyIssueDesc')}:`,
      ``,
      state.description || T('bodyNotProvided'),
    ];

    if (state.reason) {
      // Insert Reason after Resolution line (index 8 → becomes 9 after splice)
      bodyLines.splice(8, 0, `${pad(T('bodyReason'), W)}${state.reason}`);
    }

    const fileRecs = state.files.filter((f) => f.file);
    if (fileRecs.length) {
      bodyLines.push('', `${T('bodyAttachments')} (${fileRecs.length}):`);
      fileRecs.forEach(f => bodyLines.push(`  * ${f.name}  (${fmt.size(f.size)})`));
    }

    bodyLines.push('', `----------------------------------------`, `${T('bodyPage')}: ${location.href}`);
    const bodyText = bodyLines.join('\n');

    // 生成 .eml 内容存入 state（不自动下载，由用户在成功页手动点击下载）
    if (fileRecs.length > 0) {
      state._emlContent = await buildEml(subject, bodyText, fileRecs);
    } else {
      state._emlContent = null;
    }

    state._mailSubject = subject;
    state._bodyText    = bodyText;

    return { ticketId, sla: cat.sla, priority };
  }

  async function loadTracking(ticketId, email) {
    if (!CFG.endpoint) {
      // Dev mock
      state.trackingData = {
        ticketId, email, status: 'In progress',
        category: getCategory()?.label || 'Support',
        subject: state.description?.slice(0, 60) || 'Your request',
        eta: 'Aug 19',
        timeline: [
          { state: 'done',    label: 'Submitted',          time: 'Aug 17 · 09:41', detail: 'Auto-classified · Priority: High' },
          { state: 'done',    label: 'Under review',       time: 'Aug 17 · 14:22', detail: 'Support reviewed your submission.' },
          { state: 'active',  label: 'Replacement approved', time: 'Aug 17 · 15:08', detail: 'Return label emailed. New unit ships today.' },
          { state: 'pending', label: 'Replacement shipped', time: '' },
          { state: 'pending', label: 'Resolved', time: '' },
        ],
        messages: [
          { agent: true,  who: 'Marcus V. · Support', time: 'Aug 17 · 14:22', body: "Thanks for the video — I've approved a full replacement. A return label is on its way." },
          { agent: false, who: 'You',                 time: 'Aug 17 · 09:41', body: state.description || 'Original message.' },
        ],
      };
      return;
    }
    const r = await fetch(CFG.endpoint + '/tickets/' + encodeURIComponent(ticketId) + '?email=' + encodeURIComponent(email));
    if (!r.ok) throw new Error('Not found');
    state.trackingData = await r.json();
  }

  async function postReply(ticketId, email, text) {
    if (!CFG.endpoint) { await new Promise((r) => setTimeout(r, 400)); return; }
    const r = await fetch(CFG.endpoint + '/tickets/' + encodeURIComponent(ticketId) + '/reply', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, message: text }),
    });
    if (!r.ok) throw new Error('Reply failed');
  }

  // ---------- Bootstrap ----------
  document.addEventListener('DOMContentLoaded', () => {
    // Ensure toast + view container exist
    if (!$('#sc-toast')) {
      root.appendChild(h(`<div class="sc-toast" id="sc-toast"></div>`));
    }
    if (!$('#sc-view')) {
      root.appendChild(h(`<div id="sc-view"></div>`));
    }

    const params = new URLSearchParams(location.search);

    // Deep link: /pages/support?track=T-xxxx&email=xxx
    if (params.get('track')) {
      state.screen = 'track-lookup';
      render();
      // pre-fill and auto-submit
      requestAnimationFrame(() => {
        const idEl = $('#sc-t-id'); const emEl = $('#sc-t-em');
        if (idEl) idEl.value = params.get('track');
        if (emEl && params.get('email')) emEl.value = params.get('email');
      });
    } else {
      state.screen = 'wizard';
      state.step   = 0;
      render();
    }
  });
})();
